import React, { useEffect, useState, useContext, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createStyles,
  makeStyles,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Theme,
  Switch,
  FormGroup,
  FormControlLabel,
  Tooltip,
  Link,
} from '@material-ui/core';
import { Typography } from '@cbreone/core-ui';
import { externalTheme } from '@marketiq/marketiq-core-theme';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { Property, PropertyAvailabilitiesTypes } from '../../types';
import PropertyFeatureImageForComparision from '../../components/PropertyFeatureImage/PropertyFeatureImageForComparision';
import {
  getFullAddressStr,
  surveyShortlistedOverride,
  addElementIdToMUINestedElement,
  getFullAddressStr_CE_NetharLands,
  getFullAddressStr_UK,
  renderLinkFieldValue,
  getColumnHeader,
} from '../../utilities';
import InteractiveSurveyContext from '../../hooks/InteractiveSurveyContext';
import { BHPropertyFieldRule } from '../../types/bh-property-field-rule';
import { getPropertyValueForDisplay2 } from '../../utilities/property';
import { CountryKeys } from '../../map/constants/MapStyles';
import { getMediaFilesForProperties } from '../../services/interactive/data-service';
import { CE_LINK_COUNTRIES, VIEW_MODE } from '../../data/constants';

const defaultZIndex = 100;

type AvailabilityColumn = PropertyAvailabilitiesTypes & { property: Property; id?: string };

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    // new from BH
    tableCellRoot: {
      whiteSpace: 'pre-line',
      wordWrap: 'break-word',
    },
    // from CBREOne
    table: {
      minWidth: 650,
    },
    sticky: {
      position: 'sticky',
      left: 0,
      background: 'rgba(245, 247, 247, 1)',
      width: '200px',
      zIndex: defaultZIndex,
      backgroundColor: theme.palette.grey['50'],
      verticalAlign: 'top',
      '@media (max-width: 767px)': {
        width: '104px',
      },
    },
    stickyEven: {
      position: 'sticky',
      left: 0,
      background: 'rgba(230, 234, 234, 1)',
      width: '200px',
      zIndex: defaultZIndex,
      backgroundColor: theme.palette.grey['200'],
      verticalAlign: 'top',
      '@media (max-width: 767px)': {
        width: '104px',
      },
    },
    columnWidth: {
      width: '235px',
      color: theme.palette.text.primary,
      '@media (max-width: 767px)': {
        width: '114px',
      },
    },
    imageArea: {
      verticalAlign: 'top',
      backgroundColor: theme.palette.primary.contrastText,
    },
    comparisonCell: {
      height: '37px',
      borderBottom: '0px',
      padding: '5px',
      fontSize: '14px',
      wordWrap: 'break-word',
      verticalAlign: 'top',
    },
    oddRow: {
      background: theme.palette.primary.contrastText,
    },
    evenRow: {
      background: theme.palette.grey['100'],
    },
    tableContainer: {
      width: '100%',
      height: 'calc(100vh - 48px)',
      overflowY: 'auto',
    },
    leftColumnBg: {
      backgroundColor: theme.palette.grey['100'],
    },
    shortListRow: {
      '@media (min-width: 767px)': {
        alignItems: 'center',
      },
      '@media (max-width: 767px)': {
        padding: '8px',
      },
    },
    shortListToggle: {
      color: externalTheme.palette.primary.main,
      '@media (max-width: 767px)': {
        flexDirection: 'column-reverse',
        marginLeft: '0px !important',
      },
    },
    customWidth: {
      maxWidth: 200,
      backgroundColor: theme.palette.common.black,
      fontFamily: 'Calibre-R',
      fontSize: '14px',
      lineHeight: '21px',
      alignItems: 'center',
    },
    tipArrow: {
      color: theme.palette.common.black,
    },
    chip: {
      width: '10.26px',
      height: '7.82px',
      backgroundColor: externalTheme.palette.primary.main,
      color: externalTheme.palette.primary.contrastText,
    },
    icon: {
      color: externalTheme.palette.primary.main,
      width: '16.67px',
      height: '16.67px',
    },
    viewToggleLabel: {
      fontSize: '12px',
      color: '#435254',
      marginBottom: '6px',
    },
    viewToggleContainer: {
      display: 'inline-flex',
      alignItems: 'center',
      border: `1px solid ${externalTheme.palette.primary.main}`,
      borderRadius: '4px',
      overflow: 'hidden',
      marginBottom: '10px',
    },
    viewToggleBtn: {
      padding: '5px 14px',
      fontSize: '13px',
      fontFamily: 'Calibre-R',
      border: 'none',
      borderRadius: '0',
      cursor: 'pointer',
      backgroundColor: '#fff',
      color: externalTheme.palette.primary.main,
      fontWeight: 400,
    },
    viewToggleBtnActive: {
      backgroundColor: externalTheme.palette.primary.main,
      color: '#fff',
      fontWeight: 600,
    },
    viewToggleBtnDisabled: {
      backgroundColor: theme.palette.grey['200'],
      color: theme.palette.text.disabled,
      cursor: 'not-allowed',
      opacity: 0.65,
    },
  }),
);

type Props = {};

const InteractiveSurveyComparison: React.FC<Props> = () => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { survey } = useContext(InteractiveSurveyContext);
  const [shortlisted, setShortlisted] = useState(
    localStorage.getItem('shortlisted') === 'true',
  );
  const [viewMode, setViewMode] = useState<typeof VIEW_MODE[keyof typeof VIEW_MODE]>(VIEW_MODE.PROPERTY);
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  const [reducerValue, forceUpdate] = useReducer((x) => x + 1, 0);
  const tooltipTitle = t('comparison.shortlistTooltip');
  const [property, setProperty] = useState<any>({
    properties: [],
    pageMode: '',
    firstPage: true,
    activeAddressKeys: [],
    activePropertyFieldRuleList: [],
  });
  const [propertyMedia, setPropertyMedia] = useState<any>({
    propertyMedia: {},
  });

  const muiSwitchRootElement =
    document.getElementsByClassName('MuiSwitch-root')[0];
  addElementIdToMUINestedElement(muiSwitchRootElement, 'SC-Shortlist-Filter');

  const resetProperties = () => {
    if (Object.keys(survey).length > 0) {
      let properties: any[] = [];
      if (survey?.properties) {
        properties = survey.showCurrentLocation
          ? survey.properties.filter((item: any) =>
            surveyShortlistedOverride(shortlisted, item),
          )
          : survey.properties.filter(
            (item: any) =>
              surveyShortlistedOverride(shortlisted, item) &&
                !item.metaData?.isCurrentLocation,
          );
      }
      const addressKeys = ['address1', 'city', 'state', 'postalCode'];
      const activeAddressKeys = survey?.surveyPropertyFieldRule
        ?.filter((rule: Record<string, any>) =>
          addressKeys.includes(rule.fieldKey),
        )
        .map((rule: Record<string, any>) => {
          if (rule.status !== 'ACTIVE') {
            return '';
          }
          return rule.fieldKey;
        });
      const fieldRuleList = survey?.surveyPropertyFieldRule?.filter(
        (rule: Record<string, any>) =>
          rule.fieldKey !== 'name' &&
          !addressKeys.includes(rule.fieldKey) &&
          rule.status === 'ACTIVE',
      );
      setProperty({
        ...properties,
        activeAddressKeys,
        activePropertyFieldRuleList: fieldRuleList,
        properties,
      });
      if (properties.length === 0) toggleShortlisted();
    }
  };

  const getMediaFilesForSurveyProperties = async () => {
    if (survey?.properties?.length > 0) {
      const propertyIds = survey.properties.map((prop: Property) => prop.id);
      try {
        const response = await getMediaFilesForProperties({ ids: propertyIds }, survey.id);

        setPropertyMedia(response);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching media files for properties:', error);
      }
    }
  };

  useEffect(() => {
    resetProperties();
  }, [survey, reducerValue]);

  useEffect(() => {
    getMediaFilesForSurveyProperties();
  }, [survey]);

  useEffect(() => {
    window.localStorage.setItem('shortlisted', JSON.stringify(shortlisted));
  }, [shortlisted]);

  const { properties, activeAddressKeys, activePropertyFieldRuleList } =
    property;

  const toggleShortlisted = () => {
    setShortlisted(!shortlisted);
    forceUpdate();
  };

  const shortlistedDisabled = () =>
    survey.properties.filter((item) => item.metaData?.isClientShortlist)
      .length === 0;

  // Flatten all availabilities across visible properties
  const getAvailabilityColumns = (): AvailabilityColumn[] => {
    const visibleProperties = properties.filter((item: Property) =>
      surveyShortlistedOverride(shortlisted, item),
    );
    return visibleProperties.flatMap((prop: Property) =>
      (prop.availabilities || []).map((avail: PropertyAvailabilitiesTypes) => ({ ...avail, property: prop })),
    );
  };

  const isAvailability = viewMode === VIEW_MODE.AVAILABILITY;
  const availabilityColumns = isAvailability ? getAvailabilityColumns() : [];
  const hasAvailabilityColumns = getAvailabilityColumns().length > 0;

  useEffect(() => {
    if (!hasAvailabilityColumns && viewMode === VIEW_MODE.AVAILABILITY) {
      setViewMode(VIEW_MODE.PROPERTY);
    }
  }, [hasAvailabilityColumns, viewMode]);

  const displayImageRow = () => {
    const visibleProperties = properties.filter((item: Property) =>
      surveyShortlistedOverride(shortlisted, item),
    );

    return (
      <TableHead>
        <TableRow style={{ background: 'red', height: 310 }}>
            <TableCell
              className={classes.sticky}
              style={{ zIndex: defaultZIndex + 1 }}
            >
              <div>
                <Typography variant="body2" className={classes.viewToggleLabel}>
                  {t('comparison.viewDetailsBy')}
                </Typography>
                <div className={classes.viewToggleContainer}>
                  <button
                    type="button"
                    className={`${classes.viewToggleBtn} ${viewMode === VIEW_MODE.PROPERTY ? classes.viewToggleBtnActive : ''}`}
                    onClick={() => setViewMode(VIEW_MODE.PROPERTY)}
                  >
                    {t('comparison.property')}
                  </button>
                  <button
                    type="button"
                    disabled={!hasAvailabilityColumns}
                    className={`${classes.viewToggleBtn} ${viewMode === VIEW_MODE.AVAILABILITY ? classes.viewToggleBtnActive : ''} ${!hasAvailabilityColumns ? classes.viewToggleBtnDisabled : ''}`}
                    onClick={() => setViewMode(VIEW_MODE.AVAILABILITY)}
                  >
                    {t('comparison.availability')}
                  </button>
                </div>
              </div>
              {viewMode === VIEW_MODE.PROPERTY && (
                <FormGroup row className={classes.shortListRow}>
                  <Tooltip
                    open={tooltipIsOpen}
                    onOpen={() => setTooltipIsOpen(true)}
                    onClose={() => setTooltipIsOpen(false)}
                    title={tooltipTitle}
                    arrow
                    placement="top-start"
                    classes={{
                      tooltip: classes.customWidth,
                      arrow: classes.tipArrow,
                    }}
                  >
                    <InfoOutlinedIcon className={classes.icon} />
                  </Tooltip>
                  <FormControlLabel
                    className={classes.shortListToggle}
                    control={
                      <div>
                        <Switch
                          disabled={shortlistedDisabled()}
                          checked={shortlisted}
                          onChange={toggleShortlisted}
                          color="primary"
                        />
                      </div>
                    }
                    label={t('comparison.shortlistedOnly')}
                    labelPlacement="start"
                  />
                </FormGroup>
              )}
            </TableCell>
            {isAvailability
              ? availabilityColumns.map((avail: AvailabilityColumn, index: number) => {
                const prop = avail.property;
                const propValues: [string, string, string, string] = [
                  prop[activeAddressKeys[0] as keyof Property] as string,
                  prop[activeAddressKeys[1] as keyof Property] as string,
                  prop[activeAddressKeys[2] as keyof Property] as string,
                  prop[activeAddressKeys[3] as keyof Property] as string,
                ];
                const getAddressDisplay = (surveyCountry: string): string => {
                  switch (surveyCountry?.trim()) {
                    case CountryKeys.NL:
                      return getFullAddressStr_CE_NetharLands(...propValues);
                    case CountryKeys.UK:
                      return getFullAddressStr_UK(propValues[0], propValues[1], propValues[3]);
                    default:
                      return getFullAddressStr(...propValues);
                  }
                };
                const fullAddressStr = propValues ? getAddressDisplay(survey.surveyCountry) : '';
                const availTitle = avail.availabilityName || avail.availabilityTitle || `Availability ${index + 1}`;
                return (
                  <TableCell
                    key={`${prop.id}-${avail.id || index}`}
                    className={`${classes.columnWidth} ${classes.imageArea}`}
                  >
                    <PropertyFeatureImageForComparision
                      propertyId={prop.id}
                      survey={survey}
                      size="thumbnail"
                      fullAddressStr={fullAddressStr}
                      name={availTitle}
                      resetProperties={resetProperties}
                      shortlistToggle={shortlisted}
                      hideActionsAndDetails
                    />
                  </TableCell>
                );
              })
              : visibleProperties.map((propertyProp: Property) => {
                const propValues: [string, string, string, string] = [
                  propertyProp[activeAddressKeys[0] as keyof Property] as string,
                  propertyProp[activeAddressKeys[1] as keyof Property] as string,
                  propertyProp[activeAddressKeys[2] as keyof Property] as string,
                  propertyProp[activeAddressKeys[3] as keyof Property] as string,
                ];
                const getAddressDisplay = (surveyCountry: string): string => {
                  switch (surveyCountry?.trim()) {
                    case CountryKeys.NL:
                      return getFullAddressStr_CE_NetharLands(...propValues);
                    case CountryKeys.UK:
                      return getFullAddressStr_UK(propValues[0], propValues[1], propValues[3]);
                    default:
                      return getFullAddressStr(...propValues);
                  }
                };
                const fullAddressStr: string = propValues ? getAddressDisplay(survey.surveyCountry) : '';
                return (
                  <TableCell
                    key={propertyProp.id}
                    className={`${classes.columnWidth} ${classes.imageArea}`}
                  >
                    <PropertyFeatureImageForComparision
                      propertyId={propertyProp.id}
                      survey={survey}
                      size="thumbnail"
                      fullAddressStr={fullAddressStr}
                      name={propertyProp.name || '-'}
                      resetProperties={resetProperties}
                      shortlistToggle={shortlisted}
                    />
                  </TableCell>
                );
              })}
        </TableRow>
      </TableHead>
    );
  };

  const renderFloorPlansAndFlyersCellContent = (
    mediaFilesByProperty: any,
    type:string,
  ) => {
    const media = mediaFilesByProperty?.[type]?.filter(
      (mediaFile: any) => mediaFile.status === 'ACTIVE',
    ) || [];

    return (
      <Typography variant="body2">
        {media.length > 0
          ? media.map((file: any, idx: number) => (
            <React.Fragment key={file.mediaId || idx}>

               <Link
                  href={file.cloudfrontUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    wordWrap: 'break-word',
                    fontSize: '16px',
                  }}
                >
               {file?.displayName
                 ?.replace(/aftercompressing/gi, '')
                 ?.replace(/-modified/gi, '')
                 ?.replace(/(\.[^/.]+)\1$/, '$1') // Remove duplicated extension like .pdf.pdf
                 ?.trim()}
              </Link>
                 <br />
              </React.Fragment>
          ))
          : t('common.notAvailable')}
      </Typography>
    );
  };

  const displayFloorPlansAndFlyersRow = (type:string, evenRow:boolean) => {
    const visibleProperties = properties.filter((item: Property) =>
      surveyShortlistedOverride(shortlisted, item),
    );
    return (
      <TableRow className={evenRow ? classes.evenRow : classes.oddRow}>
          <TableCell
            component="th"
            className={evenRow ? `${classes.stickyEven} ${classes.comparisonCell}` : `${classes.sticky} ${classes.leftColumnBg} ${classes.comparisonCell}` }
            scope="row"
          >
           <Typography variant="body2">{type === 'fliers' ? t('comparison.flyers') : t('comparison.floorPlans')}</Typography>
          </TableCell>
          {isAvailability
            ? availabilityColumns.map((avail: AvailabilityColumn, index: number) => (
              <TableCell
                key={`${avail.property?.id}-${avail.id || index}`}
                className={`${classes.columnWidth} ${classes.comparisonCell}`}
                classes={{ root: classes.tableCellRoot }}
              >
                {renderFloorPlansAndFlyersCellContent(
                  propertyMedia.propertyMedia?.[avail.property?.id] || {},
                  type,
                )}
              </TableCell>
            ))
            : visibleProperties.map((propertyProp: Property) => (
              <TableCell
                key={propertyProp.id}
                className={`${classes.columnWidth} ${classes.comparisonCell}`}
                classes={{ root: classes.tableCellRoot }}
              >
                {renderFloorPlansAndFlyersCellContent(
                  propertyMedia.propertyMedia?.[propertyProp.id] || {},
                  type,
                )}
              </TableCell>
            ))}
      </TableRow>
    );
  };

  const tableCellStyle = (index: number) => {
    const cellStyle =
      index % 2
        ? [classes.sticky, classes.leftColumnBg].join(' ')
        : classes.stickyEven;
    return `${cellStyle} ${classes.comparisonCell}`;
  };

  const displayPropertyFieldRuleRow = (
    row: BHPropertyFieldRule,
    index: number,
  ) => {
    const visibleProperties = properties.filter((item: Property) =>
      surveyShortlistedOverride(shortlisted, item),
    );
    return (
      <TableRow
        key={row.fieldKey}
        className={`${index % 2 ? classes.oddRow : classes.evenRow}`}
      >
          <TableCell
            component="th"
            className={tableCellStyle(index)}
            scope="row"
          >
            <Typography variant="body2">{getColumnHeader(t, i18n.language, row.fieldKey, row.fieldLabel)}</Typography>
          </TableCell>
          {isAvailability
            ? availabilityColumns.map((avail: AvailabilityColumn, idx: number) => {
              const attrKey = row.fieldKey;
              const availData = avail?.data || {};
              const rawValue = availData[attrKey] || avail[attrKey as keyof PropertyAvailabilitiesTypes] || '';
              const attrValue = typeof rawValue === 'string' ? rawValue : '';
              return (
                <TableCell
                  key={`${avail.property?.id}-${avail.id || idx}`}
                  className={`${classes.columnWidth} ${classes.comparisonCell}`}
                  classes={{ root: classes.tableCellRoot }}
                >
                  {renderLinkFieldValue({
                    value: attrValue,
                    fieldLabel: row.fieldLabel,
                    surveyCountry: survey.surveyCountry,
                    propertyType: survey.propertyType,
                  })}
                </TableCell>
              );
            })
            : visibleProperties.map((propertyProp: Property) => {
              const attrKey = row.fieldKey;
              const data = propertyProp as Property & { [x: string]: any };
              const { customFields } = data;
              let attrValue = '';
              let customField = null;
              if (customFields?.length > 0) {
                customField = customFields.find(
                  (field) => field.fieldKey === row.fieldKey,
                );
              }
              if (customField) {
                attrValue = customField?.value || '';
              } else {
                attrValue = getPropertyValueForDisplay2(attrKey, data);
              }
              return (
                <TableCell
                  key={propertyProp.id}
                  className={`${classes.columnWidth} ${classes.comparisonCell}`}
                  classes={{ root: classes.tableCellRoot }}
                >
                  {renderLinkFieldValue({
                    value: attrValue,
                    fieldLabel: row.fieldLabel,
                    surveyCountry: survey.surveyCountry,
                    propertyType: survey.propertyType,
                  })}
                </TableCell>
              );
            })}
      </TableRow>
    );
  };

  return (
    <TableContainer className={classes.tableContainer}>
      <Table
        stickyHeader
        className={classes.table}
        style={{ tableLayout: 'fixed' }}
      >
        {survey?.properties && (
          <>
            {displayImageRow()}
            <TableBody>
              {CE_LINK_COUNTRIES.includes(survey.surveyCountry) && displayFloorPlansAndFlyersRow('fliers', true)}
              {CE_LINK_COUNTRIES.includes(survey.surveyCountry) && displayFloorPlansAndFlyersRow('floorPlans', false)}
            </TableBody>
          </>
        )}

        {
          // Display the rest of the property/availability attribute rows
          <TableBody>
            {viewMode === VIEW_MODE.AVAILABILITY
              ? (survey?.surveyPropertyAvailabilityRule?.filter(
                (rule: BHPropertyFieldRule) => rule.status === 'ACTIVE',
              ) || []).map((row: BHPropertyFieldRule, index: number) =>
                displayPropertyFieldRuleRow(row, index),
              )
              : activePropertyFieldRuleList.map(
                (row: BHPropertyFieldRule, index: number) =>
                  displayPropertyFieldRuleRow(row, index),
              )}
          </TableBody>
        }
      </Table>
    </TableContainer>
  );
};

InteractiveSurveyComparison.displayName = 'InteractiveSurveyComparison';
export default InteractiveSurveyComparison;
